import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Zap, Loader2, Play } from "lucide-react";
import { api } from "@/lib/api";

/** Convert ISO string → "3m ago" / "2h ago" / "5d ago". */
function formatAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

const STATUS_STYLES = {
  running:     "bg-emerald-100 text-emerald-800",
  stopped:     "bg-stone-200 text-stone-700",
  exited:      "bg-amber-100 text-amber-800",
  starting:    "bg-sky-100 text-sky-800",
  unavailable: "bg-rose-100 text-rose-800",
  unknown:     "bg-stone-100 text-stone-700",
};

/** Progress Dash tile for the wua-editorial-narrator daemon. */
export default function NarratorDaemonTile() {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/progress/editorial-narrator");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load narrator status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm text-sm text-stone-500">
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading narrator status…
      </div>
    );
  }
  if (err) {
    return (
      <div className="border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const status = data.supervisor_status || "unknown";
  const chip = STATUS_STYLES[status] || STATUS_STYLES.unknown;

  return (
    <div
      className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm"
      data-testid="tile-editorial-narrator"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-violet-50">
            <Zap className="h-5 w-5 text-violet-700" />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-900">
              Editorial narrator daemon
            </div>
            <div className="text-xs text-stone-500">
              Program: wua-editorial-narrator · Script:
              scripts/narrate_all_editorial.py
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              Window: {data.schedule?.starts_local_uae}–{data.schedule?.stops_local_uae}{" "}
              UAE ({data.schedule?.starts_utc}–{data.schedule?.stops_utc} UTC) ·
              Last narration:{" "}
              <span className="text-stone-800 font-medium">
                {formatAgo(data.last_narrated_at)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-underground-sc tracking-widest px-2 py-1 rounded ${chip}`}
            data-testid="narrator-status-chip"
          >
            {status}
          </span>
          <button
            onClick={async () => {
              try {
                await api.post(`/admin/progress/burst-daemon?program=wua-editorial-narrator&minutes=30`);
                load();
              } catch (e) { /* surfaced via reload */ }
            }}
            className="text-[11px] text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded px-2 py-1 flex items-center gap-1"
            title="Start narrator for 30 minutes"
            data-testid="narrator-burst-btn"
          >
            <Play className="h-3 w-3" /> Run 30m
          </button>
          <button
            onClick={load}
            className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
            data-testid="narrator-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <RatePanel label="Narrated · last hour"  value={data.narrated_last_hour} />
        <RatePanel label="Narrated · last 24h"   value={data.narrated_last_24h} />
        <RatePanel label="Backlog (global)"      value={data.global?.backlog} highlight />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        {["UK", "US"].map((c) => {
          const s = data.by_country?.[c] || {};
          return (
            <div
              key={c}
              className="rounded-lg border border-stone-100 p-3"
              data-testid={`narrator-country-${c.toLowerCase()}`}
            >
              <div className="text-xs text-stone-500 mb-1">
                {c} · editorial universe
              </div>
              <div className="text-2xl font-semibold text-stone-900">
                {(s.narrated || 0).toLocaleString()} /{" "}
                {(s.editorial_universe || 0).toLocaleString()}
              </div>
              <div className="mt-2 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, s.coverage_pct || 0)}%` }}
                />
              </div>
              <div className="text-[11px] text-stone-500 mt-1">
                {s.coverage_pct || 0}% narrated · {s.backlog || 0} backlog
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RatePanel({ label, value, highlight }) {
  return (
    <div className="rounded-lg border border-stone-100 p-3 bg-stone-50/40">
      <div className="text-[10px] tracking-widest text-stone-500 font-underground-sc">
        {label}
      </div>
      <div
        className={`text-2xl font-semibold ${highlight ? "text-rose-700" : "text-stone-900"}`}
      >
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}
